import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

const COUNT_SELECTOR = '[data-testid="products-count"] [role="status"]';
const CARD_SELECTOR = '[ref="cards[]"]';
const CUTOFF = 25000;

/** @type {Map<string, number>} */
const countCache = new Map();

/**
 * Build the count label using the localized plural templates exposed on the
 * count wrapper, falling back to swapping the number in the existing text.
 * @param {number} count
 * @param {string} exampleText
 * @param {HTMLElement | null} wrapper
 */
function formatCount(count, exampleText, wrapper) {
  const templateOne = wrapper?.dataset.countTemplateOne;
  const templateOther = wrapper?.dataset.countTemplateOther;

  if (templateOne && templateOther) {
    const locale = wrapper?.dataset.countLocale || document.documentElement.lang || 'en';
    let category = 'other';
    try {
      category = new Intl.PluralRules(locale).select(count);
    } catch {
      category = count === 1 ? 'one' : 'other';
    }
    const template = category === 'one' ? templateOne : templateOther;
    // Templates look like "1 item" / "2 items"; swap the leading number only.
    return template.replace(/\d[\d.,]*/, String(count));
  }

  // Fallback: replace the number without consuming the trailing space.
  const match = exampleText.match(/\d[\d.,]*/);
  if (!match) return String(count);
  return exampleText.replace(match[0], String(count));
}

/**
 * @param {string} sectionId
 * @returns {Promise<number | null>}
 */
async function countCollectionCards(sectionId) {
  const resultsList = document.querySelector(`results-list[data-collection-color-count][section-id="${sectionId}"]`);
  if (!(resultsList instanceof HTMLElement)) return null;

  const grid = resultsList.querySelector('[ref="grid"]');
  if (!(grid instanceof HTMLElement)) return null;

  const cacheKey = `${sectionId}:${window.location.pathname}${window.location.search}`;
  const cached = countCache.get(cacheKey);
  if (cached != null) return cached;

  const lastPage = Number(grid.dataset.lastPage || '1');
  const baseUrl = new URL(window.location.href);
  let total = 0;

  for (let page = 1; page <= lastPage; page += 1) {
    const pageUrl = new URL(baseUrl);
    pageUrl.searchParams.set('page', String(page));
    pageUrl.hash = '';

    const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, true, pageUrl);
    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const sectionRoot =
      doc.querySelector(`results-list[section-id="${sectionId}"]`) ||
      doc.getElementById(`shopify-section-${sectionId}`);

    if (!sectionRoot) continue;

    total += sectionRoot.querySelectorAll(CARD_SELECTOR).length;
  }

  countCache.set(cacheKey, total);
  return total;
}

/**
 * @param {string} sectionId
 */
async function updateCollectionCount(sectionId) {
  const section = document.getElementById(`shopify-section-${sectionId}`);
  const spans = section?.querySelectorAll(COUNT_SELECTOR);

  if (!spans?.length) return;

  const total = await countCollectionCards(sectionId);
  if (total == null) return;

  spans.forEach((span) => {
    if (!(span instanceof HTMLElement)) return;

    const wrapper = span.closest('[data-testid="products-count"]');
    const example = span.textContent?.trim() || '';
    if (total > CUTOFF) {
      span.textContent = example.replace(/\d[\d.,\s]*/, String(CUTOFF));
      return;
    }

    span.textContent = formatCount(total, example, wrapper instanceof HTMLElement ? wrapper : null);
  });
}

function clearCountCache() {
  countCache.clear();
}

function init() {
  document.querySelectorAll('results-list[data-collection-color-count]').forEach((el) => {
    const sectionId = el.getAttribute('section-id');
    if (!sectionId) return;
    updateCollectionCount(sectionId);
  });
}

document.addEventListener(ThemeEvents.FilterUpdate, () => {
  clearCountCache();

  document.querySelectorAll('results-list[data-collection-color-count]').forEach((el) => {
    const sectionId = el.getAttribute('section-id');
    if (!sectionId) return;

    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (!section) return;

    const observer = new MutationObserver(() => {
      observer.disconnect();
      updateCollectionCount(sectionId);
    });

    observer.observe(section, { childList: true, subtree: true });

    setTimeout(() => {
      observer.disconnect();
      updateCollectionCount(sectionId);
    }, 500);
  });
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
